/* eslint-disable dot-notation */
import { SingleIndex, assert } from '@deqo/foundation';
import { ModelSnapshot } from '@deqo/podopolis';
import { interfaces } from 'inversify';
import { idGenerator, uuid } from '../../CodesThatsGottaGoSomewhereElse';
import { Component } from './Component';
import { Convention } from './Convention';
import { ComponentDescriptor, ComponentType } from './Interfaces';
import { ObjectHost } from './ObjectHost';
import * as props from './ObjectSymbols';

export class IsolatedObjectManager {

    private nextId = idGenerator(uuid().slice(0, 6));
    private objects = new Map<string, any>();
    private indexByType = new SingleIndex<ComponentType<any>, any>();
    private indexByConvention = new SingleIndex<Convention<any>, any>();

    private store = ModelSnapshot.empty();
    private host = new IsolatedObjectHost(this.objects, this.store);

    public get(id: string): any;
    public get<T extends Component>(id: string, toc: ComponentType<T> | Convention<T>): T;
    public get(id: string, toc?: any): any {
        const inst = this.objects.get(id);
        // If an instance exists validate and return
        if (inst) {
            // If type specified, validate the type
            if (toc) {
                assert(inst.is(toc), `IsolatedObjectManager.get: ${id} cannot be resolved as ${toc}.`);
            }
            return inst;
        }
        // Throw an error if we didn't find an object.  If it does exist but isn't allocated this indicates a bigger problem.
        if (id in this.store.instances) {
            throw new Error(`ObjectManager.get: ${id} not allocated but exists in model?`);
        }
        else {
            throw new Error(`ObjectManager.get: ${id} does not exist.`);
        }
    }

    public create<T extends Component>(type: ComponentType<T>): T {
        const desc = Component.describe(type);
        const id = this.allocate(desc);
        const obj = new type();
        obj[props.Id] = id;
        obj[props.Type] = desc.name;
        obj[props.Host] = this.host;
        this.objects.set(obj.id, obj);
        if (obj instanceof Component) {
            this.indexByType.add(desc.type, obj);
            for (const conv of desc.implements) {
                this.indexByConvention.add(conv, obj);
            }
        }
        return obj;
    }

    private allocate(desc: ComponentDescriptor) {
        const id = this.nextId();
        this.store.instances[id] = { type: desc.name, variant: 'component' };
        this.store.states[id] = { '__v': '0' };
        this.store.childLinks[id] = { '__v': '0' };
        return id;
    }
}

class IsolatedObjectHost implements ObjectHost {

    constructor(
        private objects: Map<string, any>,
        private store: ModelSnapshot,
    ) { }

    public exists(id: string): boolean {
        return this.objects.has(id);
    }

    public get(id: string): Component {
        return this.objects.get(id) ?? null;
    }

    public getMaybe(id: string): Component {
        return this.objects.get(id) ?? null;
    }

    public getParent(subject: Component): Component {
        const parentId = this.store.parentLinks[subject.id];
        return !!parentId ? this.get(parentId) : null;
    }

    public getRelated(_subject: Component, _relationName: string): readonly Component[] {
        return [];
    }

    public getChildrenThisShouldBeRefactored(id: string): Readonly<Record<string, string>> {
        // Not supported for now...
        return this.store.childLinks[id] ?? { '__v': '0' };
    }

    public getState(subject: Component): Readonly<Record<string, string>> {
        return this.store.states[subject.id] ?? null;
    }

    public getAttribute(subject: Component, key: string) {
        return subject[props.Locals]?.get(key) ?? null;
    }

    public setAttribute(subject: Component, key: string, value: any): void {
        const locals = subject[props.Locals] ?? (subject[props.Locals] = new Map());
        if (value === null) {
            locals.delete(key);
        }
        else {
            locals.set(key, value);
        }
    }

    public getProperty(subject: Component, key: string) {
        return (this.getState(subject) ?? {})[key] ?? null;
    }

    public setProperty(subject: Component, key: string, value: any): void {
        const state = (this.getState(subject) ?? {}) as any;
        state[key] = value;
    }

    public append(subject: Component, child: Component, _before?: Component): void {
        // No support for ordering, FIFD.
        this.store.parentLinks[child.id] = subject.id;
        const cl = this.store.childLinks[subject.id] as any;
        const wt = Object.getOwnPropertyNames(cl).filter(x => x !== '__v').length + 1;
        cl[child.id] = `.${wt}`;
        cl['__v'] = parseInt(cl['__v']) + 1;
    }

    public truncate(_subject: Component, _child: Component): void {
        throw new Error('Not supported.');
    }

    public tick(_subject: Component): void {
    }

    public resolve<T>(_identifier: interfaces.ServiceIdentifier<T>): T {
        throw new Error('Not supported.');
    }

}
